import React, { useCallback, useEffect, useRef, useState } from "react";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

import { CircularIndeterminate, FormButton, Mapa } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  rowSelected,
  snackbarData,
  userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import ModalMapa from "components/utils/mapa/ModalMapa";
import { Grid } from "@mui/material";
import Modal from "components/UI/Modal/Modal";
import { inputTextRegexp } from "utils/regexp";
import { hotelIns, SFLocalidadGet } from "services/services-mia";
import { AddLocationAltOutlined } from "@mui/icons-material";

export default function InsertModal({ updateList, closeModal, zonas, provincias }) {
  const [inRequest, setInRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localidades, setLocalidades] = useState([]);
  const [modalMapaOpen, setModalMapaOpen] = useState(false);
  const [coordenadas, setCoordenadas] = useState({});

  const setSelected = useSetRecoilState(rowSelected);
  const setSnackbarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);

  const formikRef = useRef();

  const [isGeocoding, setIsGeocoding] = useState(false);
  const lastGeocodedRef = useRef("");

  const normalizeNombre = (str) =>
    str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() ?? "";

  const matchesNombre = (nombre, termino) => {
    const a = normalizeNombre(nombre);
    const b = normalizeNombre(termino);
    return a.includes(b) || b.includes(a);
  };

  const getSantaFeProvincia = () =>
    provincias.find((p) => matchesNombre(p.provinciaNombre, "santa fe"));

  const getRosarioLocalidad = (localidadesList) =>
    localidadesList.find((l) => matchesNombre(l.localidadSantafeNombre, "rosario"));

  const selectProvincia = useCallback(({ provinciaSantaFeId, provinciaNombre }) => {
    formikRef.current?.setFieldValue("provincia", provinciaSantaFeId);
    formikRef.current?.setFieldValue("provinciaNombre", provinciaNombre);
  }, []);

  const selectLocalidad = useCallback(({ localidadSantafeId, localidadSantafeNombre }) => {
    formikRef.current?.setFieldValue("localidad", localidadSantafeId);
    formikRef.current?.setFieldValue("localidadNombre", localidadSantafeNombre);
  }, []);

  const getLocalidades = useCallback(async (provinciaSantaFeId, { selectFirst = false } = {}) => {
    const provinciaId = Number(provinciaSantaFeId);
    if (!Number.isFinite(provinciaId)) return;

    setIsLoading(true);
    setLocalidades([]);
    formikRef.current?.setFieldValue("localidad", "");

    const { result, status } = await SFLocalidadGet({ provinciaId }, credentials);

    if (status.code !== 1) {
      setIsLoading(false);
      return;
    }
    setLocalidades(result);

    if (selectFirst && result[0]) {
      selectLocalidad(result[0]);
    } else {
      const rosario = getRosarioLocalidad(result);
      if (rosario) {
        selectLocalidad(rosario);
      } else if (result[0]) {
        selectLocalidad(result[0]);
      }
    }
    setIsLoading(false);
  }, [credentials, selectLocalidad]);

  // Replace polling with a Formik-aware watcher component below

  function GeocodeWatcher() {
    const { values } = useFormikContext();

    useEffect(() => {
      let mounted = true;
      const calle = (values?.calle || "").trim();
      const numero = (values?.numero || "").toString().trim();
      const localidadName = values?.localidadNombre || values?.localidad || "";
      const provinciaName = values?.provinciaNombre || values?.provincia || "";

      console.log('InsertModal - watcher values change:', { calle, numero, localidadName, provinciaName });

      if (!calle || calle.length < 3 || !numero) return;

      const composed = `${calle} ${numero}${localidadName ? `, ${localidadName}` : ""}${provinciaName ? `, ${provinciaName}` : ""}, Argentina`;
      if (composed === lastGeocodedRef.current) {
        console.log('InsertModal - composed equals lastGeocoded, skipping:', composed);
        return;
      }

      const timer = setTimeout(async () => {
        if (!mounted) return;
        console.log('InsertModal - geocode triggered for:', composed);
        try {
          setIsGeocoding(true);
          const results = await getGeocode({ address: composed });
          if (results && results[0]) {
            const lat = results[0].geometry.location.lat();
            const lng = results[0].geometry.location.lng();
            console.log('InsertModal - geocode result:', { lat, lng });
            setCoordenadas({ latitud: lat, longitud: lng });
            if (formikRef.current) {
              formikRef.current.setFieldValue("latitud", Number(lat).toFixed(6));
              formikRef.current.setFieldValue("longitud", Number(lng).toFixed(6));
            }
            lastGeocodedRef.current = composed;
          } else {
            console.log('InsertModal - geocode returned no results for:', composed);
          }
        } catch (err) {
          console.error("InsertModal - Geocode error:", err);
        } finally {
          if (mounted) setIsGeocoding(false);
        }
      }, 1000);

      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }, [values?.calle, values?.numero, values?.localidadNombre, values?.provinciaNombre]);

    return null;
  }

  useEffect(() => {
    if (Object.keys(credentials).length === 0 || !provincias.length) return;

    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;

      setCoordenadas({
        latitud: -32.9442426,
        longitud: -60.6504542,
      });

      const provincia = getSantaFeProvincia();
      if (!provincia?.provinciaSantaFeId) return;

      selectProvincia(provincia);
      await getLocalidades(provincia.provinciaSantaFeId);
    })();

    return () => {
      active = false;
    };
  }, [credentials, provincias, getLocalidades, selectProvincia]);

  const handleChangeProvincia = (event) => {
    const currentProvincia = provincias.find(
      (provincia) => String(provincia.provinciaSantaFeId) === String(event.target.value)
    );
    if (!currentProvincia?.provinciaSantaFeId) return;

    selectProvincia(currentProvincia);
    getLocalidades(currentProvincia.provinciaSantaFeId, { selectFirst: true });
  }

  const handleChangeLocalidad = (event) => {
    const localidadId = Number(event.target.value);
    const currentLocalidad = localidades.find((localidad) => (localidad.localidadSantafeId === localidadId))
    selectLocalidad(currentLocalidad ?? {})
  }

  const handleSetCoordenadas = ({ latitud, longitud, calle, numero, localidad, cod }) => {
    setCoordenadas({
      latitud,
      longitud
    })
    console.log({ latitud, longitud, calle, numero, localidad, cod })
    if (latitud) formikRef.current.setFieldValue("latitud", latitud.toFixed(6));
    if (longitud) formikRef.current.setFieldValue("longitud", longitud.toFixed(6));
    if (calle) formikRef.current.setFieldValue("calle", calle);
    if (numero) formikRef.current.setFieldValue("numero", numero);
    if (cod) formikRef.current.setFieldValue("cod", cod);

    if (localidad) {
      const currentLocalidad = localidades.find((l) =>
        matchesNombre(l.localidadSantafeNombre, localidad)
      );
      console.log("Localidad buscada (del mapa):", localidad);
      console.log("Localidad encontrada en lista (match):", currentLocalidad);

      if (currentLocalidad) {
        selectLocalidad(currentLocalidad);
      }
    }
  }

  const formatCuitManual = (value) => {
    // Solo permitimos números
    const onlyNums = value.replace(/\D/g, "");
    // Limitamos a 11 dígitos (el máximo de un CUIT)
    const d = onlyNums.slice(0, 11);

    // Construimos el string basándonos en la cantidad de números que hay
    if (d.length <= 2) {
      return d;
    } else if (d.length <= 10) {
      // Formateamos a: nn-nnnnnnnn
      return `${d.slice(0, 2)}-${d.slice(2)}`;
    } else {
      // Formateamos a: nn-nnnnnnnn-n
      return `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10, 11)}`;
    }
  }

  const handleChangeCuit = (e) => {
    const { value } = e.target;
    const formattedValue = formatCuitManual(value);
    formikRef.current.setFieldValue("cuit", formattedValue);
  };

  const handleToUpperCase = (fieldName) => (e) => {
    const { value } = e.target;
    formikRef.current.setFieldValue(fieldName, value.toUpperCase());
  };

  const handleSubmit = async ({
    nombre,
    calle,
    localidad,
    numero,
    cuit,
    cod,
    checkin,
    checkout
  }) => {
    setInRequest(true);

    const response = await hotelIns({
      SFHotelNombre: nombre,
      SFHotelDomicilioCalle: calle,
      SFHotelDomicilioCasa: numero,
      SFHotelDomicilioCPA: cod,
      localidadId: localidad,
      SFHotelLatitud: coordenadas.latitud.toFixed(6),
      SFHotelLongitud: coordenadas.longitud.toFixed(6),
      SFHotelCUIT: cuit,
      SFHotelCheckin: checkin,
      SFHotelCheckout: checkout,
    }, credentials);


    const functions = {
      setSnackbarInfo,
      setIsOpen: closeModal,
      setSelected,
      updateList,
    };

    handleResponse(
      response,
      "Hotel cargado exitosamente",
      functions
    );

    setInRequest(false);
  };

  const closeModalMapa = () => {
    setModalMapaOpen(false)
  }
  const cuitREGEX = /^\d{2}-\d{8}-\d{1}$/;

  const initialValues = {
    nombre: "",
    provincia: getSantaFeProvincia()?.provinciaSantaFeId ?? "",
    localidad: "",
    calle: "",
    numero: "",
    cod: "",
    cuit: "",
    checkin: "14:00",
    checkout: "10:00",
    latitud: "",
    longitud: ""
  }

  const validationSchema = yup.object({
    nombre: yup.string(FORM_ERROR_MESSAGES.text)
      .min(2, "Debe tener minimo 2 caracteres")
      .max(45, "Debe tener un maximo de 45 caracteres")
      .matches(
        inputTextRegexp,
        "Debe tener solo letras y/o números"
      )
      .required(FORM_ERROR_MESSAGES.required),
    calle: yup.string(FORM_ERROR_MESSAGES.text)
      .min(2, "Debe tener minimo 2 caracteres")
      .max(45, "Debe tener un maximo de 45 caracteres")
      .matches(
        inputTextRegexp,
        "Debe tener solo letras y/o números"
      )
      .required(FORM_ERROR_MESSAGES.required),
    numero: yup.string(FORM_ERROR_MESSAGES.text)
      .required(FORM_ERROR_MESSAGES.required),
    cuit: yup.string(FORM_ERROR_MESSAGES.text)
      .required(FORM_ERROR_MESSAGES.required)
      .test("cuit", "CUIT inválido", function (value) {
        if (!value) return true;
        if (!cuitREGEX.test(value)) return false;
        return true;
      }),
    cod: yup.string(FORM_ERROR_MESSAGES.text)
      .max(15, "Debe tener un maximo de 15 caracteres")
      .matches(
        inputTextRegexp,
        "Debe tener solo letras y/o números"
      )
      .required(FORM_ERROR_MESSAGES.required),
    checkin: yup.string(FORM_ERROR_MESSAGES.text)
      .required(FORM_ERROR_MESSAGES.required),
    checkout: yup.string(FORM_ERROR_MESSAGES.text)
      .required(FORM_ERROR_MESSAGES.required)
      .test("is-after-checkout", "El check-out debe ser menor al check-in", function (value) {
        if (value && this.parent.checkin) {
          return value < this.parent.checkin;
        }
        return true;
      })
  })

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}

      onSubmit={(values, { setSubmitting }) => {
        values.latitud = coordenadas.latitud.toFixed(6)
        values.longitud = coordenadas.longitud.toFixed(6)
        try {
          handleSubmit(values);
        } catch (e) {
          setSnackbarInfo({
            message: e.errmsg,
            severity: "error",
            open: true,
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <Form className={styles.inputs} noValidate>
        <GeocodeWatcher />
        <Modal onClose={closeModalMapa} open={modalMapaOpen} title="Seleccionar coordenadas">
          {modalMapaOpen && <ModalMapa
            closeModal={closeModalMapa}
            latitud={coordenadas.latitud}
            longitud={coordenadas.longitud}
            calle={formikRef?.current?.values?.calle}
            numero={formikRef?.current?.values?.numero}
            localidad={formikRef?.current?.values?.localidadNombre}
            provincia={formikRef?.current?.values?.provinciaNombre}
            cod={formikRef?.current?.values?.cod}
            onSetCoordenadas={handleSetCoordenadas}
          />}
        </Modal>
        <div className={styles.column}>
          <Grid container spacing={3} sx={{ alignItems: "stretch" }}>

            <Grid item md={6}>

              <Grid item md={12}>

                <Grid container spacing={3}>

                  <Grid item md={6}>
                    <FormikTextInput
                      name="nombre"
                      labelText={"Hotel"}
                      placeholder="Nombre del hotel"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <FormikTextInput
                      name="cuit"
                      labelText={"CUIT"}
                      placeholder="00-00000000-0"
                      onChange={handleChangeCuit}
                    />
                  </Grid>

                </Grid>

              </Grid>

              <Grid item md={12}>

                <Grid container spacing={3}>
                  <Grid item md={6}>
                    <FormikSelectInput
                      name="provincia"
                      labelText="Provincia"
                      onChange={handleChangeProvincia}
                    >
                      {provincias.map(({ provinciaSantaFeId, provinciaNombre }) => (
                        <option
                          key={provinciaSantaFeId}
                          data-id={provinciaSantaFeId}
                          value={provinciaSantaFeId}
                        >
                          {provinciaNombre}
                        </option>
                      ))}
                    </FormikSelectInput>
                  </Grid>
                  <Grid item md={6}>
                    <FormikSelectInput
                      name="localidad"
                      labelText="Localidad"
                      onChange={handleChangeLocalidad}
                    >
                      {localidades.map(({ localidadSantafeId, localidadSantafeNombre }) => (
                        <option
                          key={localidadSantafeId}
                          data-id={localidadSantafeId}
                          value={localidadSantafeId}
                        >
                          {localidadSantafeNombre}
                        </option>
                      ))}
                    </FormikSelectInput>
                  </Grid>

                </Grid>

              </Grid>


              <Grid item md={12}>

                <Grid container spacing={3}>

                  <Grid item md={6}>
                    <FormikTextInput
                      name="calle"
                      labelText={"Calle"}
                      placeholder={"Nombre de la calle"}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormikTextInput
                      name="numero"
                      labelText={"Número"}
                      placeholder={"Número"}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormikTextInput
                      name="cod"
                      labelText={"CPA"}
                      placeholder="Código Postal"
                      onChange={handleToUpperCase("cod")}
                    //  inputProps={{ style: { textTransform: 'uppercase' } }}
                    />
                  </Grid>

                </Grid>

              </Grid>


              <Grid item md={12}>

                <Grid container spacing={3}>
                  <Grid item md={6}>
                    <FormikTextInput
                      type={"time"}
                      name="checkin"
                      labelText={"Check-in"}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <FormikTextInput
                      type={"time"}
                      name="checkout"
                      labelText={"Check-out"}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item md={12}>
                <Grid container spacing={3}>
                  <Grid item md={6}>
                    <FormikTextInput
                      name="latitud"
                      labelText="Latitud"
                      isViewDetail={true}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <FormikTextInput
                      name="longitud"
                      labelText="Longitud"
                      isViewDetail={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>




            <Grid item md={6}>
              <Grid item md={12} height={350}>
                <div className={styles.mapPanel}>
                  <div className={styles.mapContainer}>
                    {!modalMapaOpen && (
                      <Mapa
                        lat={coordenadas.latitud}
                        lng={coordenadas.longitud}
                        style={{ position: "absolute", inset: 0 }}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    className="map-btn"
                    onClick={() => setModalMapaOpen(true)}
                    style={{ margin: "0.5rem", border: "none", display: "flex", alignItems: "center", gap: "0.2rem", backgroundColor: "transparent", cursor: "pointer" }}
                  >
                    <AddLocationAltOutlined sx={{ width: "18px", height: "18px" }} />
                    <span style={{ borderBottom: "1px solid #115D8D", fontWeight: "bold" }}>
                      Buscar coordenadas
                    </span>
                  </button>
                </div>
              </Grid>
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest || isLoading}
            newFormData={{ button: "Guardar cambios" }}
            onCloseModal={closeModal}
          />

          {(inRequest || isLoading) && <CircularIndeterminate />}
        </div>
      </Form>
    </Formik>
  );
}
